import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import ActionButton from '../../molecules/ActionButton';
import PageList from '../../templates/PageList';
import { ATableHeader } from '../../organisms/ATable';
import { currencyWithPrefix } from '../../../utils/currencyUtils';
import { formatDateToBR } from '../../../utils/DateUtils';

const BASE_URL = process.env.REACT_APP_BILLER_API_URL + '/bills';
const API_KEY = process.env.REACT_APP_API_KEY || '';

interface Bill {
    nossoNumero: string;
    externalId: string;
    pagador: {
        nomePagador: string;
        numeroDocumento: string;
    };
    dataEntrada: string;
    valor: string;
    dataVencimento: string;
    situacao: string;
    dataPagamento?: string;
    cancelamento?: {
        motivoCancelamento: string;
    };
    fileName?: string;
}

const BillerListBills = () => {
    const navigate = useNavigate();
    const [hasError, setHasError] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [sortField, setSortField] = useState('dataEntrada');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    });

    // Captura erros globais
    useEffect(() => {
        const handleError = () => setHasError(true);
        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleError);
        return () => {
            window.removeEventListener('error', handleError);
            window.removeEventListener('unhandledrejection', handleError);
        };
    }, []);

    if (hasError) {
        return (
            <div className="p-8 text-center">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
                    <h2 className="text-lg font-semibold text-red-800 mb-2">
                        Erro ao carregar dados
                    </h2>
                    <p className="text-red-600 mb-4">
                        Ocorreu um timeout na API. Tente novamente em alguns instantes.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                        Recarregar Página
                    </button>
                </div>
            </div>
        );
    }

    const url = `${BASE_URL}?dataEntrada=${selectedDate},${selectedDate}&page=${page}&pageSize=${pageSize}&order=${sortOrder}`;

    const tableHeaders: ATableHeader[] = [
        { id: 'nossoNumero', field: 'nossoNumero', title: 'Nosso Número', canSort: false, cssClass: 'text-left px-2' },
        { id: 'externalId', field: 'externalId', title: 'Ex. ID', canSort: false, cssClass: 'text-left px-2' },
        { id: 'nomePagador', field: 'pagador.nomePagador', title: 'Nome Pagador', canSort: false, cssClass: 'text-left px-2' },
        { id: 'numeroDocumento', field: 'pagador.numeroDocumento', title: 'Pagador Documento', canSort: false, cssClass: 'text-left px-2' },
        { id: 'dataEntrada', field: 'dataEntrada', title: 'Criado em', canSort: false, cssClass: 'text-left px-2' },
        { id: 'valor', field: 'valor', title: 'Valor', canSort: false, cssClass: 'text-left px-2' },
        { id: 'dataVencimento', field: 'dataVencimento', title: 'Vencimento', canSort: false, cssClass: 'text-left px-2' },
        { id: 'situacao', field: 'situacao', title: 'Situação', canSort: false },
        { id: 'dataPagamento', field: 'dataPagamento', title: 'Data Pagamento', canSort: false },
        { id: 'motivoCancelamento', field: 'cancelamento.motivoCancelamento', title: 'Cancelamento', canSort: false },
        { id: 'fileName', field: 'fileName', title: 'Boleto', canSort: false, cssClass: 'text-left px-2' }
    ];

    const handleSort = (field: string, order: 'ASC' | 'DESC') => {
        setSortField(field);
        setSortOrder(order);
    };

    const formatValue = (value: string | null | undefined): string => {
        if (!value) return '';
        const numValue = parseFloat(value);
        return currencyWithPrefix(numValue);
    };

    const formatDate = (date: string | null | undefined): string => {
        if (date)
            return formatDateToBR(date + "T00:00:00");
        return ''
    };

    const BillTD = (textAlignment: "left" | "center" | "right", children: any) => (
        <td className={`px-2 py-2 whitespace-nowrap text-sm text-${textAlignment} text-gray-900 dark:text-gray-100`}>
            {children}
        </td>
    )

    const renderRows = (bills: Bill[]) => {
        if (!bills || !Array.isArray(bills)) return null;

        return bills.map((bill, index) => (
            <tr key={bill.nossoNumero} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                {BillTD("left", bill.nossoNumero)}
                {BillTD("left", bill.externalId)}
                {BillTD("left", bill.pagador?.nomePagador)}
                {BillTD("left", bill.pagador?.numeroDocumento)}
                {BillTD("left", formatDate(bill.dataEntrada))}
                {BillTD("left", formatValue(bill.valor))}
                {BillTD("left", formatDate(bill.dataVencimento))}
                {BillTD("center",
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${bill.situacao === 'pago' ? 'bg-green-100 text-green-800' :
                        bill.situacao === 'cancelado' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                        }`}>
                        {bill.situacao || '-'}
                    </span>
                )}
                {BillTD("center", formatDate(bill.dataPagamento))}
                {BillTD("center", bill.cancelamento?.motivoCancelamento)}
                {BillTD("left", bill.fileName ? (
                    <a href="#" className="text-blue-600 hover:text-blue-800">
                        {bill.fileName}
                    </a>
                ) : "")}
            </tr>
        ));
    };

    return (
        <PageList
            key={selectedDate} // Força re-render quando data muda
            pageTitle="Lista de Boletos"
            url={url}
            fetchHeaders={[{ key: 'x-api-key', value: API_KEY }]}
            breadcrumb={[
                { label: 'Financeiro', url: '/financial' },
                { label: 'Lista de Boletos', url: '#' }
            ]}
            tableHeaders={tableHeaders}
            tableFieldSort={sortField}
            tableOrderSort={sortOrder}
            onTableSort={handleSort}
            renderRows={renderRows}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={setPageSize}
            itemsLabel="boletos"
            defaultBackendHeader={false}
            onGotApiPayload={(data) => {
                console.log('API Response:', data);
                if (data && data.list) {
                    data.headers = tableHeaders; // Força headers
                    data.size = data.pageSize || data.list.length;
                }
            }}
        >
            {{
                controlSlot: (
                    <div className="flex justify-end">
                        <ActionButton
                            label="Novo Boleto"
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={() => navigate('/financial/biller/create')}
                        />
                    </div>
                ),
                filterSlot: (
                    <div className="mb-2 flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <label htmlFor="dateFilter" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Data de Criação:
                            </label>
                            <input
                                id="dateFilter"
                                type="date"
                                value={selectedDate}
                                onChange={(e) => {
                                    setSelectedDate(e.target.value);
                                    setPage(0); // Reset página quando muda data
                                }}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                        </div>
                    </div>
                )
            }}
        </PageList>
    );
};

export default BillerListBills;